import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import '../../../dist/assets/stylesheets/css/recordPatient.css';
import { Icon } from '@iconify/react';
import { processTrial } from '../../util/trialApi';
import { formatRecordingDateTime } from '../../util/dateFormat';
import {
  getEffectiveTrialStatus,
  getTrialStageLabel,
  hasValidTrialProgress,
  shouldShowTrialProgressBar,
} from '../../util/trialStatusDisplay';
import { getCalibrationQuality, getTrackingQuality } from '../../util/trialQuality';
import { summarizeRecordingsTableTags } from '../../util/trialTagFields';
import TrialTagChips from './TrialTagChips';
import Tooltip from '@mui/material/Tooltip';

function RecordingsRow({ trial, handleView, setProcessing, onDeleteRequest, onReassignRequest, selected, onToggle, pendingProcess = false }) {
  const { id, type, name, notes, date, recorded_timezone, patient_name, patient_id } = trial;
  const params = useParams();
  const pushUrl = `/patients/review_patient/${patient_id}/${id}`;
  const forwardPageData = { backUrl: '/recordings', backName: 'Recordings' };
  const [status, setStatus] = useState(trial.status);

  useEffect(() => {
    setStatus(trial.status);
  }, [trial]);

  const rawProgress = Number(trial.progress);
  const hasValidProgress = hasValidTrialProgress(rawProgress);
  const clampedProgress = hasValidProgress ? Math.max(0, Math.min(100, rawProgress)) : 0;
  const displayStatus = getEffectiveTrialStatus(status, trial.progress, { pendingProcess });
  const showProcessingProgress = shouldShowTrialProgressBar(
    status,
    trial.progress,
    { pendingProcess }
  );

  const renderStatusCell = () => {
    const s = displayStatus || 'failed';
    const issue = trial.issue;
    if (s === 'complete') {
      return (
        <div className="calibration-status-pill calibration-status-pill--success">
          <span>Complete</span>
        </div>
      );
    }
    if (s === 'failed') {
      const tooltipContent = issue ? (
        <div style={{ fontSize: '13px', padding: '4px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>⚠ Failure Reason:</div>
          <div>{issue}</div>
        </div>
      ) : 'Processing failed.';
      return (
        <Tooltip title={tooltipContent} arrow placement="top">
          <div className="calibration-status-pill calibration-status-pill--failed">
            <span>Failed</span>
            {issue && (
              <span className="calibration-status-pill__icon">
                <Icon icon="bi:info-circle-fill" />
              </span>
            )}
          </div>
        </Tooltip>
      );
    }
    if (showProcessingProgress) {
      const stageLabel = getTrialStageLabel(clampedProgress, status, trial.stage);
      return (
        <div className="calibration-status-progress">
          <div className="calibration-status-progress__header">
            <span className="calibration-status-progress__label" title={stageLabel}>
              {stageLabel}
            </span>
            <span className="calibration-status-progress__percent">• {Math.floor(clampedProgress)}%</span>
          </div>
          <div className="calibration-status-progress__bar">
            <div
              className="calibration-status-progress__bar-fill"
              style={{ width: `${clampedProgress}%` }}
            />
          </div>
        </div>
      );
    }
    if (s === 'processing') {
      return (
        <div className="calibration-status-pill calibration-status-pill--ready">
          <span>Ready</span>
        </div>
      );
    }
    if (s === 'ready' || s === 'uploading') {
      const label = s.charAt(0).toUpperCase() + s.slice(1);
      return (
        <div className={`calibration-status-pill calibration-status-pill--${s}`}>
          <span>{label}</span>
        </div>
      );
    }
    return (
      <div className="calibration-status-pill calibration-status-pill--ready">
        <span>{s}</span>
      </div>
    );
  };

  const calibrationQuality = getCalibrationQuality(trial);
  const trackingQuality = getTrackingQuality(trial);
  const tagChips = summarizeRecordingsTableTags(trial);

  // Tier label only in the pill — numeric detail stays in the tooltip so the
  // Tags column has room beside Tracking Quality.
  const renderQualityCell = (quality) => (
    <Tooltip title={quality.title} arrow placement="top">
      <div className={`quality-pill quality-pill--${quality.tier}`}>
        <span className="quality-pill__value">
          {quality.value !== null ? quality.label : quality.display}
        </span>
      </div>
    </Tooltip>
  );

  const hanhdleProcessTrial = (trialID) => {
    setProcessing();
    processTrial(trialID);
  };
  const retry = (trialID) => {
    setProcessing();
    processTrial(trialID);
  };
  const generateActionBtns = (status, trialID) => {
    switch (status) {
      case 'complete':
        return <><button onClick={() => handleView(trialID)} className='svg-btn'><Icon icon="carbon:view" />View</button></>
      case 'processing':
      case 'queued':
        return <><button onClick={() => handleView(trialID)} className='svg-btn'><Icon icon="carbon:view" />View</button></>
      case 'ready':
        return <><button onClick={() => hanhdleProcessTrial(trialID)} className='svg-btn'><Icon icon="ion:push-outline" />Process</button><button onClick={() => handleView(trialID)} className='svg-btn'><Icon icon="carbon:view" />View</button></>
      case 'failed':
        return <><button onClick={() => retry(trialID)} className='svg-btn'><Icon icon="gg:redo" />Retry</button><button onClick={() => handleView(trialID)} className='svg-btn'><Icon icon="carbon:view" />View</button></>
      case 'uploading':
        return ''
      default: return <><button onClick={() => handleView(trialID)} className='svg-btn'><Icon icon="carbon:view" />View</button></>
    }
  };

  return (
    <>
      <tr className='patient-row'>
        {onToggle != null && (
          <td className="table-cell-checkbox" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onToggle(id)}
              aria-label={`Select trial ${id}`}
            />
          </td>
        )}
        <td>
          <Link to={{ pathname: pushUrl, state: forwardPageData }}>
            <div>{id}</div>
          </Link>
        </td>
        <td>
          <Link to={{ pathname: pushUrl, state: forwardPageData }}>
            <div>{patient_name}</div>
          </Link>
        </td>
        <td>
          <Link to={{ pathname: pushUrl, state: forwardPageData }}>
            <div>{formatRecordingDateTime(date, recorded_timezone, { withSeconds: true })}</div>
          </Link>
        </td>
        <td>
          <Link to={{ pathname: pushUrl, state: forwardPageData }}>
            {renderStatusCell()}
          </Link>
        </td>
        <td className="quality-cell">
          <Link to={{ pathname: pushUrl, state: forwardPageData }}>
            {renderQualityCell(calibrationQuality)}
          </Link>
        </td>
        <td className="quality-cell">
          <Link to={{ pathname: pushUrl, state: forwardPageData }}>
            {renderQualityCell(trackingQuality)}
          </Link>
        </td>
        <td className="tags-cell">
          <Link to={{ pathname: pushUrl, state: forwardPageData }}>
            <TrialTagChips
              items={tagChips}
              className="recordings-tags-chips"
              emptyLabel="—"
            />
          </Link>
        </td>
        <td>
          <div className='actions'>
            {generateActionBtns(displayStatus, id)}
            {onReassignRequest && (
              <button
                type="button"
                className="svg-btn reassign-patient-btn"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onReassignRequest(trial); }}
                title="Change patient"
                aria-label="Change patient for this recording"
              >
                <Icon icon="mdi:account-switch" />
              </button>
            )}
            {onDeleteRequest && (
              <button
                type="button"
                className="svg-btn delete-icon-btn"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDeleteRequest(id); }}
                title="Delete"
                aria-label="Delete recording"
              >
                <Icon icon="uiw:delete" />
              </button>
            )}
          </div>
        </td>
      </tr>
    </>
  )
}

export default RecordingsRow;
